import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

const COMMON_DIFFICULTY = 0;
const COLS = 9;
const ROWS = 5;

interface PieceSpec
{
  type: number;
  x: number;
  y?: number;
  width: number;
  height: number;
  variant?: number;
}

function makeLayer( specs: PieceSpec[], cols = COLS ): LevelLayer
{
  const grid = Array.from( { length: ROWS }, () => Array( cols ).fill( 0 ) );
  const pieces: LevelPiece[] = [];

  specs.forEach( ( spec, index ) =>
  {
    const id = index + 1;
    const y = spec.y ?? ROWS - spec.height;
    const piece: LevelPiece = {
      id,
      type: spec.type as LevelPieceType,
      x: spec.x,
      y,
      width: spec.width,
      height: spec.height,
      variant: spec.variant,
    };

    for ( let row = y; row < y + spec.height; row++ )
      for ( let col = spec.x; col < spec.x + spec.width; col++ )
        grid[row][col] = id;

    pieces.push( piece );
  } );

  return { grid, pieces };
}

const NEW_BLOCK_MODELS_LEVELS: LevelConfig[] = [
  {
    level: 90400,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ makeLayer( [
      { type: 1, x: 0, width: 1, height: 4, variant: 0 },
      { type: 1, x: 2, width: 1, height: 4, variant: 2 },
      { type: 9, x: 4, width: 1, height: 4, variant: 0 },
      { type: 2, x: 6, width: 1, height: 4 },
    ] ) ],
  },
  {
    level: 90401,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ makeLayer( [
      { type: 1, x: 0, width: 1, height: 5, variant: 0 },
      { type: 1, x: 2, width: 1, height: 5, variant: 2 },
      { type: 9, x: 4, width: 1, height: 5, variant: 1 },
      { type: 2, x: 6, width: 1, height: 5 },
    ] ) ],
  },
  {
    level: 90402,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ makeLayer( [
      { type: 1, x: 0, width: 2, height: 2, variant: 0 },
      { type: 1, x: 2, width: 1, height: 1, variant: 0 },
      { type: 9, x: 3, width: 2, height: 2, variant: 2 },
      { type: 9, x: 5, width: 1, height: 1, variant: 0 },
      { type: 2, x: 6, width: 2, height: 2 },
      { type: 2, x: 8, width: 1, height: 1 },
    ] ) ],
  },
  {
    level: 90403,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ makeLayer( [
      { type: 7, x: 1, width: 1, height: 4 },
      { type: 7, x: 4, width: 4, height: 1 },
    ] ) ],
  },
  {
    level: 90404,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ makeLayer( [
      { type: 7, x: 1, width: 1, height: 5 },
      { type: 7, x: 3, width: 5, height: 1 },
    ] ) ],
  },
  {
    level: 90405,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    // Five depth slices form a diamond footprint on the round tabletop. Keep
    // every piece at 1x1, 1x2 or 1x3 until the larger block art is approved.
    layers: [ makeLayer( [ { type: 7, x: 2, width: 1, height: 2 } ], 5 ) ],
    tables: [ {
      shape: "round",
      layers: [
        makeLayer( [ { type: 7, x: 2, width: 1, height: 2 } ], 5 ),
        makeLayer( [
          { type: 1, x: 1, width: 1, height: 1, variant: 0 },
          { type: 9, x: 2, width: 1, height: 2, variant: 1 },
          { type: 2, x: 3, width: 1, height: 1 },
        ], 5 ),
        makeLayer( [
          { type: 1, x: 0, width: 1, height: 1, variant: 2 },
          { type: 7, x: 1, width: 1, height: 2 },
          { type: 9, x: 2, width: 1, height: 3, variant: 0 },
          { type: 1, x: 3, width: 1, height: 2, variant: 0 },
          { type: 2, x: 4, width: 1, height: 1 },
        ], 5 ),
        makeLayer( [
          { type: 2, x: 1, width: 1, height: 1 },
          { type: 9, x: 2, width: 1, height: 2, variant: 4 },
          { type: 1, x: 3, width: 1, height: 1, variant: 2 },
        ], 5 ),
        makeLayer( [ { type: 7, x: 2, width: 1, height: 2 } ], 5 ),
      ],
    } ],
  },
  {
    level: 90406,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    layers: [ makeLayer( [
      { type: 9, x: 0, y: 3, width: 2, height: 2, variant: 0 },
      { type: 9, x: 2, y: 3, width: 2, height: 2, variant: 1 },
      { type: 9, x: 4, y: 3, width: 2, height: 2, variant: 2 },
      { type: 9, x: 6, y: 3, width: 2, height: 2, variant: 3 },
      { type: 9, x: 0, y: 1, width: 2, height: 2, variant: 4 },
      { type: 9, x: 2, y: 1, width: 2, height: 2, variant: 5 },
      { type: 9, x: 4, y: 1, width: 2, height: 2, variant: 6 },
      { type: 9, x: 6, y: 1, width: 2, height: 2, variant: 7 },
    ], 8 ) ],
  },
  {
    level: 90407,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    // Stone in every size it ships in: 1x1-1x5 and 2x2. The table holds nine
    // columns, so the tall pieces stand in the back row with a gap between them
    // and 1x1/1x2 stand in the front row, facing those gaps.
    layers: [
      makeLayer( [
        { type: 2, x: 0, width: 1, height: 5 },
        { type: 2, x: 2, width: 1, height: 4 },
        { type: 2, x: 4, width: 1, height: 3 },
        { type: 2, x: 6, width: 2, height: 2 },
      ] ),
      makeLayer( [
        { type: 2, x: 1, width: 1, height: 1 },
        { type: 2, x: 3, width: 1, height: 2 },
      ] ),
    ],
  },
  {
    level: 90408,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    // Brick (Cube variant 1) in every size it ships in: 1x1-1x5 and 2x2. The table holds nine
    // columns, so the tall pieces stand in the back row with a gap between them
    // and 1x1/1x2 stand in the front row, facing those gaps.
    layers: [
      makeLayer( [
        { type: 1, x: 0, width: 1, height: 5, variant: 1 },
        { type: 1, x: 2, width: 1, height: 4, variant: 1 },
        { type: 1, x: 4, width: 1, height: 3, variant: 1 },
        { type: 1, x: 6, width: 2, height: 2, variant: 1 },
      ] ),
      makeLayer( [
        { type: 1, x: 1, width: 1, height: 1, variant: 1 },
        { type: 1, x: 3, width: 1, height: 2, variant: 1 },
      ] ),
    ],
  },  {
    level: 90409,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    // Column in every size it ships in, upright, then a portico: two 1x3
    // columns carrying a lying 1x4 with a 1x1 on top.
    layers: [ makeLayer( [
      { type: 12, x: 0, width: 1, height: 1 },
      { type: 12, x: 1, width: 1, height: 2 },
      { type: 12, x: 2, width: 1, height: 3 },
      { type: 12, x: 3, width: 1, height: 4 },
      { type: 12, x: 3, y: 0, width: 1, height: 1 },
      { type: 12, x: 5, width: 1, height: 3 },
      { type: 12, x: 8, width: 1, height: 3 },
      { type: 12, x: 5, y: 1, width: 4, height: 1 },
      { type: 12, x: 7, y: 0, width: 1, height: 1 },
    ] ) ],
  },
  {
    level: 90410,
    difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
    // Round column, same layout as the square one: every size upright, then
    // a portico of two 1x3 columns carrying a lying 1x4 with a 1x1 on top.
    layers: [ makeLayer( [
      { type: 13, x: 0, width: 1, height: 1 },
      { type: 13, x: 1, width: 1, height: 2 },
      { type: 13, x: 2, width: 1, height: 3 },
      { type: 13, x: 3, width: 1, height: 4 },
      { type: 13, x: 3, y: 0, width: 1, height: 1 },
      { type: 13, x: 5, width: 1, height: 3 },
      { type: 13, x: 8, width: 1, height: 3 },
      { type: 13, x: 5, y: 1, width: 4, height: 1 },
      { type: 13, x: 7, y: 0, width: 1, height: 1 },
    ] ) ],
  },
];

export class NewBlockModelsShowcase
{
  public static readonly TESTS = [
    { label: "BASIC 1x4: BOX / PLASTIC / STONE", level: 90400 },
    { label: "BASIC 1x5: BOX / PLASTIC / STONE", level: 90401 },
    { label: "BASIC 2x2 + 1x1: BOX / PLASTIC / STONE", level: 90402 },
    { label: "TREE 1x4", level: 90403 },
    { label: "TREE 1x5", level: 90404 },
    { label: "ROUND TABLE (1x1-1x3 ONLY)", level: 90405 },
    { label: "PLASTIC 2x2: ALL COLORS", level: 90406 },
    { label: "STONE: 1x1-1x5 + 2x2", level: 90407 },
    { label: "BRICK: 1x1-1x5 + 2x2", level: 90408 },
    { label: "COLUMN: 1x1-1x4 + LYING", level: 90409 },
    { label: "ROUND COLUMN: 1x1-1x4 + LYING", level: 90410 },
  ] as const;

  public static activate( index: number ): number
  {
    return this.TESTS[index]?.level ?? this.TESTS[0].level;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return NEW_BLOCK_MODELS_LEVELS.find( config => config.level === level ) ?? null;
  }
}
